import { Add } from "@mui/icons-material";
import { Box, Button, Chip, CircularProgress, Container, Stack, Typography } from "@mui/material";
import type { SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataTable } from "@/components/ui/data-table";
import { DataTableToolbar } from "@/components/ui/data-table-toolbar";
import { useDataTable } from "@/hooks/use-data-table";
import { useFindAllDeliveryOptionsQuery } from "@/libs/generated/hooks";
import type { DeliveryOptions } from "@/types/models/delivery.types";
import { type DeliveryOptionRecord, getDeliveryOptionsColumns } from "./columns";

function DeliveryOptionsTable() {
  "use no memo";

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isPending: loading, error } = useFindAllDeliveryOptionsQuery();

  const options = data?.findAllDeliveryOptions ?? [];
  const [searchParams, setSearchParams] = useSearchParams();

  const columns = getDeliveryOptionsColumns(t, {
    onEdit: (record) => navigate(`/admin/options/${record.id}/edit`),
  });
  const handleSortingChange = (sorting: SortingState) => {
    const nextParams = new URLSearchParams(searchParams);
    const nextSort = sorting[0];

    nextParams.delete("page");

    if (nextSort !== undefined) {
      nextParams.set("orderBy", String(nextSort.id));
      nextParams.set("order", nextSort.desc ? "desc" : "asc");
    } else {
      nextParams.delete("orderBy");
      nextParams.delete("order");
    }

    setSearchParams(nextParams);
  };
  const orderBy = (searchParams.get("orderBy") as keyof DeliveryOptions | null) ?? "id";
  const order = searchParams.get("order") === "asc" ? "asc" : "desc";
  const { table } = useDataTable<DeliveryOptionRecord>({
    data: options as DeliveryOptionRecord[],
    columns,
    manualFiltering: true,
    manualSorting: true,
    onSortingChange: handleSortingChange,
    externalSorting: [{ id: orderBy, desc: order === "desc" }],
    pageCount: -1,
    initialState: {
      columnVisibility: {
        sortOrder: false, // hidden by default
        createdAt: false, // you can hide multiple
        code: false, // example of hiding the code column by default
      },
    },
  });

  if (loading) {
    return (
      <Box alignItems="center" display="flex" justifyContent="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Typography color="error" variant="body1">
        {t("deliveryOption.table.errorLoading", {
          error: error instanceof Error ? error.message : t("common.error.generic"),
        })}
      </Typography>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography component="h1" sx={{ textTransform: "capitalize" }} variant="h4">
          {t("layouts.header.options")}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            color="primary"
            label={`${options.length} ${t("common.shared.total")}`}
            variant="outlined"
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/admin/options/new")}
          >
            {t("deliveryOption.table.newOption")}
          </Button>
        </Stack>
      </Stack>

      <DataTable table={table} totalCount={options.length}>
        <DataTableToolbar table={table} />
      </DataTable>
    </Container>
  );
}

export default DeliveryOptionsTable;
